import React, { useEffect, useState } from "react";
import { useI18n } from "../lib/i18n";
import { Link } from "react-router-dom";
import api from "../lib/api";
import {
  CalendarDays, Plane, Cake, PartyPopper, ArrowLeftCircle,
  Clock, Receipt, TrendingUp, GraduationCap, MessageSquare, ChevronRight,
  AlertTriangle, AlertCircle, Info, ShieldAlert,
} from "lucide-react";

/* ──────────────────────────────────────────────────────────────────
   CalendarTab — list view grouped by month
   ────────────────────────────────────────────────────────────────── */
const EVENT_STYLE = {
  pto:         { icon: CalendarDays, color: "text-[#4338ca]", bg: "bg-[#e0e7ff]", label: "dash.hint_pto" },
  vacation:    { icon: Plane,         color: "text-[#0e7490]", bg: "bg-[#cffafe]", label: "cockpit.ev_vacation" },
  return:      { icon: ArrowLeftCircle, color: "text-[#166534]", bg: "bg-[#dcfce7]", label: "cockpit.ev_return" },
  birthday:    { icon: Cake,          color: "text-[#be185d]", bg: "bg-[#fce7f3]", label: "cockpit.ev_birthday" },
  anniversary: { icon: PartyPopper,   color: "text-[#a38d5b]", bg: "bg-[#fcf3e2]", label: "cockpit.ev_anniversary" },
};

function groupByMonth(events) {
  const groups = {};
  for (const e of events) {
    const ym = (e.start_date || "").slice(0, 7);
    if (!groups[ym]) groups[ym] = [];
    groups[ym].push(e);
  }
  return Object.entries(groups).sort();
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso + "T12:00:00");
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
}
function formatMonth(ym) {
  if (!ym) return "—";
  const [y, m] = ym.split("-");
  const d = new Date(parseInt(y), parseInt(m) - 1, 1);
  return d.toLocaleDateString("pt-BR", { month: "long", year: "numeric" }).replace(/^./, (c) => c.toUpperCase());
}

export function CalendarTab({ scope }) {
  const { t } = useI18n();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/cockpit/calendar?scope=${scope}`)
      .then((r) => setEvents(r.data))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [scope]);

  if (loading) return <div className="text-[#94a3b8] py-8 text-center">{t("cockpit.loading_calendar")}</div>;
  if (events.length === 0) return (
    <div className="sw-card text-center py-12" data-testid="cockpit-calendar-empty">
      <CalendarDays size={32} className="text-[#cbd5e1] mx-auto mb-3" />
      <div className="text-[14px] text-[#64748b]">{t("cockpit.no_events_120")}</div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="cockpit-calendar-tab">
      {groupByMonth(events).map(([ym, items]) => (
        <div key={ym} className="sw-card !p-0 overflow-hidden">
          <div className="bg-[#f6f6f6] px-5 py-3 text-[11px] uppercase tracking-wider text-[#475569] font-semibold border-b border-[#eeeded]">
            {formatMonth(ym)} · {items.length} {items.length === 1 ? "evento" : "eventos"}
          </div>
          <ul className="divide-y divide-[#f1f5f9]">
            {items.map((e) => {
              const style = EVENT_STYLE[e.type] || EVENT_STYLE.pto;
              const Icon = style.icon;
              const range = e.start_date !== e.end_date ? `${formatDate(e.start_date)} → ${formatDate(e.end_date)}` : formatDate(e.start_date);
              return (
                <li key={e.entity_id} className="px-5 py-3 flex items-center gap-3 hover:bg-[#fafafa]" data-testid={`cal-event-${e.entity_id}`}>
                  <div className={`shrink-0 w-9 h-9 rounded-full ${style.bg} flex items-center justify-center ${style.color}`}>
                    <Icon size={15} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13.5px] font-medium text-[#1e293b] truncate">
                      {e.title}
                      {e.external && (
                        <span className="ml-2 inline-flex px-1.5 py-0.5 rounded-full bg-[#fcf3e2] text-[#a38d5b] text-[9.5px] font-semibold uppercase tracking-wide">
                          {t("ext.provider_badge", { name: e.provider_tenant_name || "" })}
                        </span>
                      )}
                    </div>
                    <div className="text-[12px] text-[#64748b]">{range}{e.status === "pending" && " · pendente aprovação"}</div>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${style.bg} ${style.color}`}>{t(style.label)}</span>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
   TimelineTab — chronological feed of team activity
   ────────────────────────────────────────────────────────────────── */
const TIMELINE_STYLE = {
  pto_approved:     { icon: CalendarDays, color: "text-[#166534]", bg: "bg-[#dcfce7]" },
  pto_rejected:     { icon: CalendarDays, color: "text-[#9f1239]", bg: "bg-[#fee2e2]" },
  reimb_approved:   { icon: Receipt,      color: "text-[#166534]", bg: "bg-[#dcfce7]" },
  reimb_rejected:   { icon: Receipt,      color: "text-[#9f1239]", bg: "bg-[#fee2e2]" },
  reimb_submitted_on_redteam: { icon: Receipt, color: "text-[#4338ca]", bg: "bg-[#e0e7ff]" },
  reimb_paid:       { icon: Receipt,      color: "text-[#a38d5b]", bg: "bg-[#fcf3e2]" },
  eval_completed:   { icon: TrendingUp,   color: "text-[#4338ca]", bg: "bg-[#e0e7ff]" },
  training_completed: { icon: GraduationCap, color: "text-[#a38d5b]", bg: "bg-[#fcf3e2]" },
  one_on_one:       { icon: MessageSquare, color: "text-[#475569]", bg: "bg-[#e2e8f0]" },
};

function relativeTime(iso) {
  if (!iso) return "—";
  const dt = new Date(iso);
  const diff = (Date.now() - dt.getTime()) / 1000;
  if (diff < 60) return "agora";
  if (diff < 3600) return `${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d`;
  return dt.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
}

export function TimelineTab({ scope }) {
  const { t } = useI18n();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/cockpit/timeline?scope=${scope}&limit=80`)
      .then((r) => setEvents(r.data))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [scope]);

  if (loading) return <div className="text-[#94a3b8] py-8 text-center">{t("cockpit.loading_timeline")}</div>;
  if (events.length === 0) return (
    <div className="sw-card text-center py-12" data-testid="cockpit-timeline-empty">
      <Clock size={32} className="text-[#cbd5e1] mx-auto mb-3" />
      <div className="text-[14px] text-[#64748b]">{t("cockpit.no_activity_60")}</div>
    </div>
  );

  return (
    <div className="sw-card !p-0 overflow-hidden" data-testid="cockpit-timeline-tab">
      <ul className="divide-y divide-[#f1f5f9]">
        {events.map((e) => {
          const style = TIMELINE_STYLE[e.type] || TIMELINE_STYLE.one_on_one;
          const Icon = style.icon;
          return (
            <li key={e.entity_id + e.type} className="px-5 py-3 flex items-start gap-3 hover:bg-[#fafafa]" data-testid={`tl-${e.entity_id}`}>
              <div className={`shrink-0 w-9 h-9 rounded-full ${style.bg} flex items-center justify-center ${style.color}`}>
                <Icon size={15} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13.5px] text-[#1e293b]">
                  <span className="font-medium">{e.user_name}</span>
                  <span className="text-[#94a3b8] mx-1.5">·</span>
                  <span>{e.summary}</span>
                </div>
                <div className="text-[11px] text-[#94a3b8] mt-0.5">{relativeTime(e.at)}</div>
              </div>
              {e.link && (
                <Link to={e.link} className="text-[#475569] hover:text-[#1e293b]"><ChevronRight size={16} /></Link>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
   RiskFlagsTab — categorized alerts
   ────────────────────────────────────────────────────────────────── */
const SEVERITY = {
  critical: { icon: ShieldAlert,   color: "text-[#9f1239]", bg: "bg-[#fee2e2]", ring: "border-l-[#9f1239]", label: "cockpit.critical" },
  warning:  { icon: AlertTriangle, color: "text-[#a38d5b]", bg: "bg-[#fcf3e2]", ring: "border-l-[#a38d5b]", label: "cockpit.attention" },
  info:     { icon: Info,          color: "text-[#475569]", bg: "bg-[#e2e8f0]", ring: "border-l-[#475569]", label: "cockpit.info_label" },
};

export function RiskFlagsTab({ scope }) {
  const { t } = useI18n();
  const [data, setData] = useState({ critical: [], warning: [], info: [], counts: { critical: 0, warning: 0, info: 0 } });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/cockpit/risk-flags?scope=${scope}`)
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [scope]);

  if (loading) return <div className="text-[#94a3b8] py-8 text-center">{t("cockpit.loading_risk")}</div>;

  const total = (data.counts?.critical || 0) + (data.counts?.warning || 0) + (data.counts?.info || 0);
  if (total === 0) {
    return (
      <div className="sw-card text-center py-12" data-testid="cockpit-risks-empty">
        <ShieldAlert size={32} className="text-[#22c55e] mx-auto mb-3" />
        <div className="text-[14px] text-[#64748b]">Sem riscos identificados na equipe agora. 🎉</div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="cockpit-risks-tab">
      {/* Counts header */}
      <div className="grid grid-cols-3 gap-3">
        {["critical", "warning", "info"].map((sev) => {
          const cfg = SEVERITY[sev];
          const SevIcon = cfg.icon;
          return (
            <div key={sev} className={`sw-card !p-4 flex items-center gap-3 ${cfg.bg}/40`} data-testid={`risks-count-${sev}`}>
              <div className={`w-10 h-10 rounded-full ${cfg.bg} flex items-center justify-center ${cfg.color}`}>
                <SevIcon size={18} />
              </div>
              <div>
                <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#94a3b8] font-semibold">{t(cfg.label)}</div>
                <div className="font-semibold text-[22px] text-[#1e293b] leading-none mt-1">{data.counts[sev]}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Sections */}
      {["critical", "warning", "info"].map((sev) => {
        const items = data[sev] || [];
        if (items.length === 0) return null;
        const cfg = SEVERITY[sev];
        return (
          <div key={sev} className="sw-card !p-0 overflow-hidden">
            <div className="bg-[#f6f6f6] px-5 py-3 text-[11px] uppercase tracking-wider font-semibold border-b border-[#eeeded] flex items-center gap-2">
              <span className={cfg.color}>{t(cfg.label)}</span>
              <span className="text-[#94a3b8]">({items.length})</span>
            </div>
            <ul className="divide-y divide-[#f1f5f9]">
              {items.map((f) => {
                const SevIcon = cfg.icon;
                return (
                  <li
                    key={f.id}
                    className={`px-5 py-3 flex items-start gap-3 hover:bg-[#fafafa] border-l-2 ${cfg.ring}`}
                    data-testid={`risk-${f.id}`}
                  >
                    <div className={`shrink-0 w-9 h-9 rounded-full ${cfg.bg} flex items-center justify-center ${cfg.color}`}>
                      <SevIcon size={15} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13.5px] font-medium text-[#1e293b]">{f.title}</div>
                      <div className="text-[12px] text-[#64748b]">
                        <span className="font-medium text-[#475569]">{f.user_name}</span>
                        {f.detail && <span> · {f.detail}</span>}
                      </div>
                    </div>
                    {f.link && (
                      <Link to={f.link} className="text-[#475569] hover:text-[#1e293b] shrink-0"><ChevronRight size={16} /></Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
